import { IBaseTyreOffer, NillishObject } from '@wyz/types';
import { Button, Col, Flex, Label, Row, Text } from '@wyz/ui';
import { serialize } from '@wyz/utils';
import {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  type HTMLAttributes,
} from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { CheckboxField, NumberField } from '~/common/components';
import { Trans, useApplicationContext, useI18n } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { SearchErrors, SearchValuePreview } from '~/purchase/components';
import { MIN_CART_QUANTITY, PurchaseSearchHelpers } from '~/purchase/helpers';
import { useSearchTrigger } from '~/purchase/hooks';
import { IBaseSearchFormSchema, ISearchTypeArgs } from '~/purchase/types';

interface SearchTriggerContextValue {
  canSearch: boolean;
  isProcessing: boolean;
  count: number | undefined;
  maxSearchExceeded: boolean;
  hasNoResults: boolean;
}

const SearchTriggerContext = createContext<
  SearchTriggerContextValue | undefined
>(undefined);

const useSearchTriggerContext = () => {
  const context = useContext(SearchTriggerContext);
  if (!context) {
    throw new Error(
      'SearchTrigger compound components must be used within SearchTrigger',
    );
  }
  return context;
};

interface SearchTriggerProps {
  searchType: ISearchTypeArgs['searchType'];
  customer: IUserCustomer;
  children: ReactNode;
}

function SearchTrigger({ searchType, customer, children }: SearchTriggerProps) {
  const { canSearch, count, errors } = useSearchTrigger<IBaseTyreOffer>({
    searchType,
    customer,
  });

  const contextValue = useMemo<SearchTriggerContextValue>(
    () => ({
      canSearch,
      isProcessing: count.isProcessing,
      count: count.data?.data,
      hasNoResults: errors.hasNoResults,
      maxSearchExceeded: errors.maxSearchExceeded,
    }),
    [
      canSearch,
      count.isProcessing,
      count.data?.data,
      errors.hasNoResults,
      errors.maxSearchExceeded,
    ],
  );

  return (
    <SearchTriggerContext.Provider value={contextValue}>
      {children}
    </SearchTriggerContext.Provider>
  );
}

interface LayoutProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

function Layout({ children, className = '', ...props }: LayoutProps) {
  return (
    <div className={`search-bar-trigger row mt-3 ${className}`} {...props}>
      {children}
    </div>
  );
}

function Body() {
  const { t } = useI18n();
  const { auth } = useApplicationContext();
  const methods = useFormContext<IBaseSearchFormSchema>();
  const watchedValues = useWatch({
    control: methods.control,
  }) as IBaseSearchFormSchema;
  const { canSearch, isProcessing } = useSearchTriggerContext();

  return (
    <Flex alignItems='center' justifyContent='between'>
      <SearchValuePreview
        label={<Trans i18nKey='app.purchase.search_page.selected_dimension' />}
        value={
          PurchaseSearchHelpers.formatDimension({
            width: watchedValues.width,
            height: watchedValues.height,
            diameter: watchedValues.diameter,
            loadIndex: watchedValues.loadIndexes?.[0],
            speedIndex: watchedValues.speedIndexes?.[0],
            identifier: watchedValues.identifier?.value,
          }) ?? t('app.common.none')
        }
      />

      <Row className='no-label-margin'>
        <Col>
          <CheckboxField
            label={
              <Text heading='xs' color='secondary-gray' className='text-nowrap'>
                <Trans i18nKey='app.common.search.include_higher_indexes' />
              </Text>
            }
            name='includeHigherIndexes'
            disabled={!watchedValues.vehicleTypeGroup}
            formGroupClassName=''
            asTextField={false}
          />
        </Col>
        <Col>
          <Flex className='space-x-2'>
            <Label htmlFor={'quantity'} className='text-nowrap required mt-2'>
              <Trans i18nKey='app.common.quantity' />
            </Label>
            <div className='w-100'>
              <NumberField
                name='quantity'
                required
                min={MIN_CART_QUANTITY}
                max={200}
                size='sm'
              />
            </div>
          </Flex>
        </Col>
        <Col>
          <Flex alignItems='start' className='space-x-2'>
            <Button
              type='submit'
              id='search'
              form='purchase-search-form'
              variant='primary'
              disabled={!canSearch}
              isLoading={isProcessing}
              size='sm'
            >
              <Trans i18nKey='app.common.buttons.search' />
            </Button>
            {auth.isWyzUser && (
              <Button
                type='button'
                id='debug-diagnostic'
                variant='secondary'
                disabled={!canSearch}
                isLoading={isProcessing}
                size='sm'
                onClick={() => {
                  const quantity = methods.getValues('quantity');
                  if (!quantity) {
                    void methods.trigger('quantity');
                    return;
                  }
                  const searchQuery = {
                    ...watchedValues,
                  } as NillishObject<IBaseSearchFormSchema>;
                  const qs = serialize(
                    PurchaseSearchHelpers.serializeSearchToQs(searchQuery),
                  );
                  window.open(`/purchase/diagnosis?${qs}`, '_blank');
                }}
              >
                <Trans i18nKey='app.common.buttons.diagnosis' />
              </Button>
            )}
          </Flex>
        </Col>
      </Row>
    </Flex>
  );
}

function ErrorSection() {
  const { maxSearchExceeded, hasNoResults, count } = useSearchTriggerContext();

  return (
    <SearchErrors
      maxSearchExceeded={maxSearchExceeded}
      hasNoResults={hasNoResults}
      count={count}
    />
  );
}

const PurchaseSearchTriggerBar = Object.assign(SearchTrigger, {
  Layout,
  Body,
  ErrorSection,
});

export default PurchaseSearchTriggerBar;
